import { useState, useCallback, useMemo } from 'react'
import {
  PageHeader,
  Button,
  Card,
  LoadingState,
  ErrorState,
  EmptyState,
  DataTable,
  Modal,
  Input,
  Textarea,
  Select,
  useToast,
  ConfirmDialog,
  StatusBadge,
} from '@/components/ui'
import { MapPin, Plus, Pencil, Trash2, Mail, Send, Search, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, startOfWeek, endOfWeek, addWeeks } from 'date-fns'
import { de } from 'date-fns/locale'
import type { ColumnDef } from '@tanstack/react-table'
import { DispositionCalendar } from './components/DispositionCalendar/DispositionCalendar.tsx'
import type { DispoCalendarEvent } from '@shared/types'

interface AusseneinsatzRecord {
  id: string
  tenantId: string
  customerId: string | null
  technicianUserId: string
  technicianName: string
  customerName: string
  customerEmail: string | null
  customerContact: string | null
  ticketId: string | null
  topic: string
  location: string | null
  scheduledDate: string
  scheduledStart: string | null
  scheduledEnd: string | null
  note: string | null
  emailStatus: 'none' | 'pending' | 'sent' | 'confirmed' | 'cancelled'
  emailSentAt: string | null
  emailTemplateId: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
}

interface EmailTemplate {
  id: string
  name: string
  subject: string
  body: string
  isActive: boolean
}

interface InternalUser {
  id: string
  name: string
  email: string
}

interface CustomerOption {
  id: string
  name: string
  tenantId: string | null
}

interface AvailabilitySuggestion {
  technicianUserId: string
  technicianName: string
  date: string
  startTime: string
  endTime: string
  durationMinutes: number
  travelBefore: number
  travelAfter: number
  blockStart: string
  blockEnd: string
}

const EMAIL_STATUS_MAP: Record<string, string> = {
  none: 'Keine E-Mail',
  pending: 'Ausstehend',
  sent: 'Gesendet',
  confirmed: 'Bestaetigt',
  cancelled: 'Storniert',
}

function formatDate(d: string) {
  try {
    const date = typeof d === 'string' && d.length > 10 ? new Date(d) : new Date(d + 'T00:00:00')
    return format(date, 'dd.MM.yyyy', { locale: de })
  } catch {
    return d
  }
}

function weekRange(date: Date): { from: Date; to: Date } {
  return {
    from: startOfWeek(date, { weekStartsOn: 1 }),
    to: endOfWeek(date, { weekStartsOn: 1 }),
  }
}

async function fetchAusseneinsaetze(from: string, to: string): Promise<AusseneinsatzRecord[]> {
  const res = await axios.get<{ data: AusseneinsatzRecord[] }>('/api/dispo/ausseneinsatz', {
    params: { from, to },
  })
  return res.data.data
}

async function fetchEmailTemplates(): Promise<EmailTemplate[]> {
  const res = await axios.get<{ data: EmailTemplate[] }>('/api/dispo/email-templates')
  return res.data.data.filter((t) => t.isActive)
}

async function fetchAllUsers(): Promise<InternalUser[]> {
  const res = await axios.get<InternalUser[]>('/api/users', { params: { portalKind: 'internal' } })
  return res.data
}

async function fetchUsersByTenant(tenantId: string): Promise<InternalUser[]> {
  const res = await axios.get<InternalUser[]>('/api/users', { params: { tenantId } })
  return res.data
}

async function searchCustomers(q: string): Promise<CustomerOption[]> {
  const res = await axios.get<Record<string, unknown>[]>('/api/customers', { params: { search: q } })
  return res.data.map((c) => ({
    id: c.id as string,
    name: c.name as string,
    tenantId: (c.tenantId as string | null | undefined) ?? (c.tenant_id as string | null | undefined) ?? null,
  }))
}

interface FormState {
  technicianUserId: string
  technicianName: string
  customerId: string
  customerName: string
  customerEmail: string
  customerContact: string
  customerTenantId: string
  ticketId: string
  topic: string
  location: string
  scheduledDate: string
  scheduledStart: string
  scheduledEnd: string
  note: string
  emailTemplateId: string
}

function defaultForm(): FormState {
  return {
    technicianUserId: '',
    technicianName: '',
    customerId: '',
    customerName: '',
    customerEmail: '',
    customerContact: '',
    customerTenantId: '',
    ticketId: '',
    topic: '',
    location: '',
    scheduledDate: format(new Date(), 'yyyy-MM-dd'),
    scheduledStart: '',
    scheduledEnd: '',
    note: '',
    emailTemplateId: '',
  }
}

export function DispoAusseneinsatzPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Shared week state – drives both calendar and table
  const [weekFrom, setWeekFrom] = useState<Date>(() => weekRange(new Date()).from)
  const weekTo = useMemo(() => endOfWeek(weekFrom, { weekStartsOn: 1 }), [weekFrom])

  const dateFrom = format(weekFrom, 'yyyy-MM-dd')
  const dateTo = format(weekTo, 'yyyy-MM-dd')

  // Modal state
  const [formOpen, setFormOpen] = useState(false)
  const [editRecord, setEditRecord] = useState<AusseneinsatzRecord | null>(null)
  const [form, setForm] = useState<FormState>(defaultForm())
  const [deleteTarget, setDeleteTarget] = useState<AusseneinsatzRecord | null>(null)
  const [emailModal, setEmailModal] = useState<AusseneinsatzRecord | null>(null)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')

  // Customer search state (in form)
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false)

  // Availability search state
  const [availOpen, setAvailOpen] = useState(false)
  const [availDuration, setAvailDuration] = useState('60')
  const [availFrom, setAvailFrom] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [availTo, setAvailTo] = useState(format(addWeeks(new Date(), 2), 'yyyy-MM-dd'))
  const [availTechnicianUserId, setAvailTechnicianUserId] = useState('')
  const [availSuggestions, setAvailSuggestions] = useState<AvailabilitySuggestion[] | null>(null)
  const [availMissingHours, setAvailMissingHours] = useState<string[]>([])
  const [availSearching, setAvailSearching] = useState(false)
  const [availError, setAvailError] = useState<string | null>(null)

  const queryKey = ['dispo-ausseneinsatz', dateFrom, dateTo]

  const { data: records, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => fetchAusseneinsaetze(dateFrom, dateTo),
    staleTime: 30_000,
  })

  const { data: emailTemplates } = useQuery({
    queryKey: ['dispo-email-templates-active'],
    queryFn: fetchEmailTemplates,
    staleTime: 60_000,
  })

  const { data: allUsers } = useQuery({
    queryKey: ['internal-users'],
    queryFn: fetchAllUsers,
    staleTime: 120_000,
  })

  // Technicians filtered by the selected customer's tenant (or all users when no customer)
  const { data: tenantUsers } = useQuery({
    queryKey: ['internal-users-tenant', form.customerTenantId],
    queryFn: () => form.customerTenantId ? fetchUsersByTenant(form.customerTenantId) : Promise.resolve(null),
    enabled: !!form.customerTenantId,
    staleTime: 120_000,
  })

  const availableTechnicians = tenantUsers ?? allUsers ?? []

  // Customer search query
  const { data: customerResults } = useQuery({
    queryKey: ['customers-search', customerSearch],
    queryFn: () => customerSearch.length >= 2 ? searchCustomers(customerSearch) : Promise.resolve([]),
    enabled: customerSearch.length >= 2,
    staleTime: 30_000,
  })

  const users = allUsers ?? []
  const userOptions = availableTechnicians.map((u) => ({ value: u.id, label: u.name }))
  const availTechOptions = [
    { value: '__all__', label: 'Alle Techniker' },
    ...availableTechnicians.map((u) => ({ value: u.id, label: u.name })),
  ]
  const templateOptions = [
    { value: '__none__', label: 'Keine Vorlage' },
    ...(emailTemplates ?? []).map((t) => ({ value: t.id, label: t.name })),
  ]

  const handleWeekChange = useCallback((from: Date) => {
    setWeekFrom(from)
  }, [])

  const handleCalendarEventClick = useCallback((event: DispoCalendarEvent) => {
    if (event.type !== 'AUSSENEINSATZ') return
    const ausseneinsatzId = event.originalData?.ausseneinsatzId as string | undefined
    if (!ausseneinsatzId) return
    const record = records?.find((r) => r.id === ausseneinsatzId)
    if (record) openEdit(record)
  }, [records]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        technicianUserId: form.technicianUserId,
        technicianName:
          form.technicianName ||
          users.find((u) => u.id === form.technicianUserId)?.name ||
          '',
        customerId: form.customerId || null,
        customerName: form.customerName,
        customerEmail: form.customerEmail || null,
        customerContact: form.customerContact || null,
        ticketId: form.ticketId || null,
        topic: form.topic,
        location: form.location || null,
        scheduledDate: form.scheduledDate,
        scheduledStart: form.scheduledStart || null,
        scheduledEnd: form.scheduledEnd || null,
        note: form.note || null,
        emailTemplateId: form.emailTemplateId === '__none__' ? null : form.emailTemplateId || null,
      }
      if (editRecord) {
        return axios.patch(`/api/dispo/ausseneinsatz/${editRecord.id}`, payload)
      }
      return axios.post('/api/dispo/ausseneinsatz', payload)
    },
    onSuccess: () => {
      toast({ title: editRecord ? 'Einsatz gespeichert' : 'Einsatz erstellt', tone: 'success' })
      queryClient.invalidateQueries({ queryKey: ['dispo-ausseneinsatz'] })
      queryClient.invalidateQueries({ queryKey: ['dispo-kalender'] })
      setFormOpen(false)
      setEditRecord(null)
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast({ title: msg ?? 'Fehler beim Speichern', tone: 'error' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axios.delete(`/api/dispo/ausseneinsatz/${id}`),
    onSuccess: () => {
      toast({ title: 'Einsatz geloescht', tone: 'success' })
      queryClient.invalidateQueries({ queryKey: ['dispo-ausseneinsatz'] })
      queryClient.invalidateQueries({ queryKey: ['dispo-kalender'] })
    },
    onError: () => {
      toast({ title: 'Fehler beim Loeschen', tone: 'error' })
    },
  })

  const sendEmailMutation = useMutation({
    mutationFn: ({ id, subject, body }: { id: string; subject: string; body: string }) =>
      axios.post(`/api/dispo/ausseneinsatz/${id}/send-email`, { subject, body }),
    onSuccess: (res) => {
      const sentTo = (res.data as { sentTo?: string }).sentTo
      toast({
        title: `E-Mail gesendet${sentTo ? ` an ${sentTo}` : ''}`,
        tone: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['dispo-ausseneinsatz'] })
      setEmailModal(null)
    },
    onError: () => {
      toast({ title: 'Fehler beim Senden der E-Mail', tone: 'error' })
    },
  })

  function openCreate() {
    setEditRecord(null)
    setForm(defaultForm())
    setCustomerSearch('')
    setAvailSuggestions(null)
    setAvailOpen(false)
    setFormOpen(true)
  }

  function openEdit(r: AusseneinsatzRecord) {
    setEditRecord(r)
    setForm({
      technicianUserId: r.technicianUserId,
      technicianName: r.technicianName,
      customerId: r.customerId ?? '',
      customerName: r.customerName,
      customerEmail: r.customerEmail ?? '',
      customerContact: r.customerContact ?? '',
      customerTenantId: r.tenantId,
      ticketId: r.ticketId ?? '',
      topic: r.topic,
      location: r.location ?? '',
      scheduledDate: r.scheduledDate,
      scheduledStart: r.scheduledStart ?? '',
      scheduledEnd: r.scheduledEnd ?? '',
      note: r.note ?? '',
      emailTemplateId: r.emailTemplateId ?? '__none__',
    })
    setCustomerSearch(r.customerName)
    setAvailSuggestions(null)
    setAvailOpen(false)
    setFormOpen(true)
  }

  function selectCustomer(customer: CustomerOption) {
    setForm((f) => ({
      ...f,
      customerId: customer.id,
      customerName: customer.name,
      customerTenantId: customer.tenantId ?? '',
      // Reset technician when customer (and thus tenant) changes
      technicianUserId: f.customerTenantId !== (customer.tenantId ?? '') ? '' : f.technicianUserId,
      technicianName: f.customerTenantId !== (customer.tenantId ?? '') ? '' : f.technicianName,
    }))
    setCustomerSearch(customer.name)
    setCustomerSearchOpen(false)
  }

  function applyAvailabilitySuggestion(s: AvailabilitySuggestion) {
    setForm((f) => ({
      ...f,
      technicianUserId: s.technicianUserId,
      technicianName: s.technicianName,
      scheduledDate: s.date,
      scheduledStart: s.startTime,
      scheduledEnd: s.endTime,
    }))
    setAvailSuggestions(null)
    setAvailOpen(false)
  }

  async function runAvailabilitySearch() {
    if (!form.customerTenantId && !form.customerId) {
      toast({ title: 'Bitte zuerst einen Kunden auswählen', tone: 'error' })
      return
    }
    setAvailSearching(true)
    setAvailError(null)
    setAvailSuggestions(null)
    try {
      const res = await axios.post<{
        suggestions: AvailabilitySuggestion[]
        techniciansMissingWorkingHours: string[]
        warning?: string
      }>('/api/dispo/ausseneinsatz/availability-search', {
        tenantId: form.customerTenantId || undefined,
        customerId: form.customerId || undefined,
        durationMinutes: parseInt(availDuration, 10) || 60,
        from: availFrom,
        to: availTo,
        technicianUserId: availTechnicianUserId && availTechnicianUserId !== '__all__' ? availTechnicianUserId : undefined,
      })
      setAvailSuggestions(res.data.suggestions)
      setAvailMissingHours(res.data.techniciansMissingWorkingHours ?? [])
      if (res.data.warning) {
        toast({ title: res.data.warning, tone: 'warning' })
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setAvailError(msg ?? 'Fehler bei der Terminsuche')
    } finally {
      setAvailSearching(false)
    }
  }

  function openEmailModal(r: AusseneinsatzRecord) {
    const template = emailTemplates?.find((t) => t.id === r.emailTemplateId)
    const vars: Record<string, string> = {
      customerName: r.customerName,
      technicianName: r.technicianName,
      scheduledDate: formatDate(r.scheduledDate),
      scheduledTime: r.scheduledStart ?? '',
      location: r.location ?? '',
      topic: r.topic,
      note: r.note ?? '',
    }
    function replacePlaceholders(text: string) {
      return text.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? `{{${k}}}`)
    }
    const subject = replacePlaceholders(template?.subject ?? 'Ausseneinsatz-Termin')
    const body = replacePlaceholders(
      template?.body ?? 'Sehr geehrte/r ' + r.customerName + ',\n\nTermin: ' + formatDate(r.scheduledDate)
    )
    setEmailSubject(subject)
    setEmailBody(body)
    setEmailModal(r)
  }

  const columns: ColumnDef<AusseneinsatzRecord>[] = [
    {
      accessorKey: 'scheduledDate',
      header: 'Datum',
      cell: ({ row }) => (
        <span className="font-medium text-sm">{formatDate(row.original.scheduledDate)}</span>
      ),
    },
    {
      id: 'time',
      header: 'Zeit',
      cell: ({ row }) => (
        <span className="text-sm text-gray-600">
          {row.original.scheduledStart
            ? `${row.original.scheduledStart}${row.original.scheduledEnd ? ` – ${row.original.scheduledEnd}` : ''}`
            : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'technicianName',
      header: 'Techniker',
      cell: ({ row }) => <span className="text-sm">{row.original.technicianName}</span>,
    },
    {
      accessorKey: 'customerName',
      header: 'Kunde',
      cell: ({ row }) => (
        <div>
          <div className="text-sm font-medium">{row.original.customerName}</div>
          {row.original.customerContact && (
            <div className="text-xs text-gray-500">{row.original.customerContact}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'topic',
      header: 'Thema',
      cell: ({ row }) => (
        <span className="text-sm text-gray-700 truncate max-w-xs block">{row.original.topic}</span>
      ),
    },
    {
      accessorKey: 'emailStatus',
      header: 'E-Mail',
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.emailStatus}
          label={EMAIL_STATUS_MAP[row.original.emailStatus] ?? row.original.emailStatus}
        />
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1.5 justify-end">
          {row.original.customerEmail && (
            <Button
              size="xs"
              variant="ghost"
              title="E-Mail senden"
              onClick={() => openEmailModal(row.original)}
            >
              <Send className="w-3.5 h-3.5 text-blue-600" />
            </Button>
          )}
          <Button size="xs" variant="ghost" onClick={() => openEdit(row.original)}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button size="xs" variant="ghost" onClick={() => setDeleteTarget(row.original)}>
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Ausseneinsatz-Planung"
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1.5" />
            Neuer Einsatz
          </Button>
        }
      />

      {/* Week calendar — Task 1 */}
      <DispositionCalendar
        from={weekFrom}
        showHeader
        showEmployeeImages
        tenantId={undefined}
        onWeekChange={(from) => handleWeekChange(from)}
        onEventClick={handleCalendarEventClick}
      />

      {/* Ausseneinsatz list for the same week */}
      <Card>
        <div className="px-4 py-3 border-b text-sm text-gray-600">
          Außeneinsätze {format(weekFrom, 'dd.MM.', { locale: de })} – {format(weekTo, 'dd.MM.yyyy', { locale: de })}
        </div>

        {isLoading && <LoadingState label="Einsaetze werden geladen..." />}
        {error && <ErrorState title="Fehler beim Laden der Einsaetze" details={String(error)} />}

        {!isLoading && !error && (
          <>
            {!records?.length ? (
              <EmptyState
                icon={<MapPin className="w-8 h-8" />}
                title="Keine Ausseneinsaetze in dieser Woche"
                description="Erstellen Sie einen neuen Ausseneinsatz oder wechseln Sie die Woche im Kalender oben."
                action={
                  <Button variant="primary" onClick={openCreate}>
                    <Plus className="w-4 h-4 mr-1.5" />
                    Ersten Einsatz erstellen
                  </Button>
                }
              />
            ) : (
              <DataTable columns={columns} data={records} />
            )}
          </>
        )}
      </Card>

      {/* Create / Edit modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editRecord ? 'Einsatz bearbeiten' : 'Neuen Einsatz erstellen'}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setFormOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={() => saveMutation.mutate()}
              disabled={
                saveMutation.isPending ||
                !form.technicianUserId ||
                !form.customerName.trim() ||
                !form.topic.trim() ||
                !form.scheduledDate
              }
            >
              {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Customer selector — Task 2 */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kunde *
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Kundenname eingeben (min. 2 Zeichen)..."
              value={customerSearch}
              onChange={(e) => {
                setCustomerSearch(e.target.value)
                setCustomerSearchOpen(true)
                if (!e.target.value) {
                  setForm((f) => ({ ...f, customerId: '', customerName: '', customerTenantId: '' }))
                }
              }}
              onFocus={() => setCustomerSearchOpen(true)}
              onBlur={() => setTimeout(() => setCustomerSearchOpen(false), 150)}
            />
            {form.customerTenantId && (
              <p className="text-xs text-gray-500 mt-1">
                Mandant: <span className="font-medium">{form.customerTenantId}</span>
              </p>
            )}
            {customerSearchOpen && (customerResults?.length ?? 0) > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                {customerResults!.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                    onMouseDown={() => selectCustomer(c)}
                  >
                    <span>{c.name}</span>
                    {c.tenantId && (
                      <span className="text-xs text-gray-400 ml-2 truncate">Mandant vorhanden</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {/* Fallback: free text customerName when no customer selected from list */}
            {!form.customerId && customerSearch && (
              <p className="text-xs text-amber-600 mt-0.5">
                Kein Kunde aus der Liste gewählt – Freitext wird verwendet.
                <button
                  type="button"
                  className="ml-1 underline"
                  onClick={() => setForm((f) => ({ ...f, customerName: customerSearch }))}
                >
                  Übernehmen
                </button>
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Ansprechpartner"
              value={form.customerContact}
              onChange={(e) => setForm((f) => ({ ...f, customerContact: e.target.value }))}
              placeholder="Name des Ansprechpartners"
            />
            <Input
              label="Kunden-E-Mail"
              type="email"
              value={form.customerEmail}
              onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))}
              hint="Fuer Terminbestaetigung per E-Mail"
            />
          </div>

          {/* Technician — filtered by the customer's tenant */}
          <Select
            label="Techniker *"
            value={form.technicianUserId}
            onValueChange={(v) => {
              const user = availableTechnicians.find((u) => u.id === v)
              setForm((f) => ({
                ...f,
                technicianUserId: v,
                technicianName: user?.name ?? f.technicianName,
              }))
            }}
            options={userOptions}
            placeholder="Techniker waehlen..."
            hint={form.customerTenantId ? 'Nur Techniker dieses Mandanten werden angezeigt' : undefined}
          />

          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Datum *"
              type="date"
              value={form.scheduledDate}
              onChange={(e) => setForm((f) => ({ ...f, scheduledDate: e.target.value }))}
            />
            <Input
              label="Von (Uhrzeit)"
              type="time"
              value={form.scheduledStart}
              onChange={(e) => setForm((f) => ({ ...f, scheduledStart: e.target.value }))}
            />
            <Input
              label="Bis (Uhrzeit)"
              type="time"
              value={form.scheduledEnd}
              onChange={(e) => setForm((f) => ({ ...f, scheduledEnd: e.target.value }))}
            />
          </div>

          <Input
            label="Thema / Aufgabe *"
            value={form.topic}
            onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
            placeholder="Kurze Beschreibung des Einsatzes"
          />
          <Input
            label="Einsatzort"
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="Adresse oder Beschreibung"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Ticket-ID"
              value={form.ticketId}
              onChange={(e) => setForm((f) => ({ ...f, ticketId: e.target.value }))}
              placeholder="TANSS-Ticket"
            />
            <Select
              label="E-Mail-Vorlage"
              value={form.emailTemplateId || '__none__'}
              onValueChange={(v) => setForm((f) => ({ ...f, emailTemplateId: v }))}
              options={templateOptions}
            />
          </div>
          <Textarea
            label="Notiz (intern)"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            rows={3}
            placeholder="Interne Anmerkungen zum Einsatz..."
          />

          {/* Availability search — Task 3 */}
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100"
              onClick={() => setAvailOpen((o) => !o)}
            >
              <span className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                Verfügbare Termine suchen (automatisch)
              </span>
              {availOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {availOpen && (
              <div className="p-4 space-y-3 border-t border-gray-200">
                {!form.customerTenantId && !form.customerId && (
                  <p className="text-xs text-amber-600">
                    Bitte zuerst einen Kunden aus der Liste auswählen, damit nur Techniker des richtigen Mandanten berücksichtigt werden.
                  </p>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label="Einsatzdauer (Min.)"
                    type="number"
                    value={availDuration}
                    onChange={(e) => setAvailDuration(e.target.value)}
                    hint="z.B. 60 für 1 Stunde"
                  />
                  <Input
                    label="Suche von"
                    type="date"
                    value={availFrom}
                    onChange={(e) => setAvailFrom(e.target.value)}
                  />
                  <Input
                    label="Suche bis"
                    type="date"
                    value={availTo}
                    onChange={(e) => setAvailTo(e.target.value)}
                  />
                </div>
                <Select
                  label="Techniker einschränken (optional)"
                  value={availTechnicianUserId || '__all__'}
                  onValueChange={(v) => setAvailTechnicianUserId(v === '__all__' ? '' : v)}
                  options={availTechOptions}
                />
                <Button
                  variant="secondary"
                  onClick={runAvailabilitySearch}
                  disabled={availSearching}
                  size="sm"
                >
                  <Search className="w-4 h-4 mr-1.5" />
                  {availSearching ? 'Suche läuft...' : 'Termine suchen'}
                </Button>

                {availError && (
                  <p className="text-xs text-red-600">{availError}</p>
                )}

                {availMissingHours.length > 0 && (
                  <p className="text-xs text-amber-600">
                    Keine TANSS-Arbeitszeitdaten für: {availMissingHours.join(', ')} — diese Techniker wurden nicht berücksichtigt.
                  </p>
                )}

                {availSuggestions !== null && (
                  <div className="space-y-2 max-h-48 overflow-auto">
                    {availSuggestions.length === 0 ? (
                      <p className="text-sm text-gray-500">Keine verfügbaren Termine im gewählten Zeitraum gefunden.</p>
                    ) : (
                      availSuggestions.slice(0, 20).map((s, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-3 py-2 text-xs"
                        >
                          <div>
                            <span className="font-medium">{format(new Date(s.date), 'EEE, dd.MM.', { locale: de })}</span>{' '}
                            {s.startTime}–{s.endTime} Uhr · {s.technicianName}
                            <span className="text-gray-500 ml-2">(inkl. {s.travelBefore} Min. Anfahrt)</span>
                          </div>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => applyAvailabilitySuggestion(s)}
                            title="Diesen Termin übernehmen"
                          >
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </Button>
                        </div>
                      ))
                    )}
                    {availSuggestions.length > 20 && (
                      <p className="text-xs text-gray-500">{availSuggestions.length - 20} weitere Vorschläge – Suchzeitraum einschränken für mehr Übersicht.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Email preview & send modal */}
      <Modal
        open={!!emailModal}
        onClose={() => setEmailModal(null)}
        title="E-Mail an Kunden senden"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEmailModal(null)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (emailModal) {
                  sendEmailMutation.mutate({
                    id: emailModal.id,
                    subject: emailSubject,
                    body: emailBody,
                  })
                }
              }}
              disabled={sendEmailMutation.isPending}
            >
              <Mail className="w-4 h-4 mr-1.5" />
              {sendEmailMutation.isPending ? 'Senden...' : 'Jetzt senden'}
            </Button>
          </div>
        }
      >
        {emailModal && (
          <div className="space-y-3">
            <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
              <div>
                <span className="text-gray-500">An:</span>{' '}
                <span className="font-medium">{emailModal.customerEmail}</span>
              </div>
              {emailModal.customerContact && (
                <div>
                  <span className="text-gray-500">Ansprechpartner:</span>{' '}
                  {emailModal.customerContact}
                </div>
              )}
            </div>
            <Input
              label="Betreff"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
            <Textarea
              label="E-Mail-Text"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              rows={12}
            />
            <p className="text-xs text-gray-500">
              Im Dev-Modus wird die E-Mail an die Dev-Adresse umgeleitet.
            </p>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id)
          setDeleteTarget(null)
        }}
        title="Einsatz loeschen"
        description={`Moechten Sie den Einsatz bei "${deleteTarget?.customerName}" am ${deleteTarget ? formatDate(deleteTarget.scheduledDate) : ''} wirklich loeschen?`}
        confirmLabel="Loeschen"
        variant="danger"
      />
    </div>
  )
}
